import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { IProfileFull } from '@project/view-models';
import { EUserPermission, PERMISSIONS_BY_PROFILE_ROLE } from '@project/shared';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { AuthorisationStateService } from './authorisation-state.service';
import { ProfileApiProviderService } from '@project/data-providers';

@Injectable({
  providedIn: 'root',
})
export class UserProfileDataService {
  private _profile$ = new BehaviorSubject<IProfileFull | null>(null);
  public profile$ = this._profile$.asObservable();

  private _profileFillRequired: boolean | null = null;
  private _profileGroupFillRequired: boolean | undefined = undefined;
  private _profileRequest$: Observable<IProfileFull> | null = null;

  constructor(
    private authorisationStateService: AuthorisationStateService,
    private profileApiProviderService: ProfileApiProviderService,
  ) {}

  get profile(): IProfileFull | null {
    return this._profile$.value;
  }

  isProfileGroupFillRequired(): boolean | undefined {
    return this._profileGroupFillRequired;
  }

  isProfileFillRequired(): boolean | null {
    return this._profileFillRequired;
  }

  updateProfileDate(userData: Partial<IProfileFull>) {
    this._profile$.next({ ...this._profile$.value, ...userData });
  }

  setProfileData(userData: IProfileFull | null) {
    this._profile$.next(userData);
  }

  getProfile$(): Observable<IProfileFull> {
    if (this._profile$.value) {
      return of(this._profile$.value);
    }

    if (this._profileRequest$) {
      return this._profileRequest$;
    }

    this._profileRequest$ = this.profileApiProviderService.getMyProfileInformation().pipe(
      tap((profile) => this.setProfileData(profile)),
      finalize(() => (this._profileRequest$ = null)),
      shareReplay(1),
    );

    return this._profileRequest$;
  }

  isDoctorActive(): Observable<boolean> {
    return this.profile$.pipe(map((profile) => profile?.doctorStatus === 'Active'));
  }

  isDoctor(): Observable<boolean> {
    return this.profile$.pipe(map((profile) => profile?.isDoctor));
  }

  setIsProfileDataFillRequired(fillRequired: boolean) {
    this._profileFillRequired = fillRequired;
  }

  setIsProfileGroupFillRequired(fillRequired: boolean) {
    this._profileGroupFillRequired = fillRequired;
  }

  clear() {
    this._profile$.next(null);
    this._profileRequest$ = null;
    this._profileFillRequired = null;
    this._profileGroupFillRequired = undefined;
  }

  hasPermissions(permissions: EUserPermission[]): boolean {
    const userPermissions = this.getUserPermissions();
    return permissions.every((permission) => userPermissions.includes(permission));
  }

  hasPermissions$(permissions: EUserPermission[]): Observable<boolean> {
    return this.profile$.pipe(map(() => this.hasPermissions(permissions)));
  }

  hasAtLeastOnePermission(permissions: EUserPermission[]): boolean {
    const userPermissions = this.getUserPermissions();
    return permissions.some((permission) => userPermissions.includes(permission));
  }

  hasAtLeastOnePermission$(permissions: EUserPermission[]): Observable<boolean> {
    return this.profile$.pipe(map(() => this.hasAtLeastOnePermission(permissions)));
  }

  private getUserPermissions(): EUserPermission[] {
    const profileType = this.authorisationStateService.getAuthorizationData()?.userType;
    return PERMISSIONS_BY_PROFILE_ROLE[profileType] || [];
  }
}
